import { getCurrentTenantConfig } from "../config/tenants";

export enum BREAKPOINT {
  MOBILE = 480,
  TABLET = 768,
  DESKTOP = 992,
  XL = 1200,
}

// Constant map of codes to labels checkout/checkin reasons
const EARLY_RELEASE_CODE_DESCRIPTION = {
  NON_ENGAGE360: "Non-Engage 360 Enrichment Program",
  ACADEMIC_SUPPORT: "Academic Support (tutoring, intervention, etc.)",
  FAMILY_EMERGENCY: "Family Emergency",
  MEDICAL_APPOINTMENT: "Medical Appointment",
  WEATHER: "Weather Condition",
  CHILD_HEALTH: "Child Accident/Illness",
  WALKER_RELEASE: "Transportation/Intermediate Walker Release",
  HIGHSCHOOL_EXEMPT: "High School Exempt",
  HIGH_IMPACT_TUTORING: "High Impact Tutoring",
  OTHER: "Other Reason",
};

/**
 * Built-in early-release / late-arrival codes, used when a tenant has configured no
 * custom codes of its own.
 *
 * `HIGHSCHOOL_EXEMPT` and `HIGH_IMPACT_TUTORING` carry no `hmn` value BY DECISION,
 * not by oversight: they are admin-only codes with no counterpart anywhere in the
 * parent app, so no Hmong translation exists on the platform. They fall back to
 * English via `localizedCodeDescription`, which is the agreed behavior. The other
 * eight are transplanted from `frontend-user/dictionaries/hmn.json`, whose English
 * source strings are byte-identical to the `description` values here.
 */
export const EARLY_RELEASE_CODE_DESCRIPTION_WITH_TRANSLATIONS = [
  {
    code: "NON_ENGAGE360",
    description: "Non-Engage 360 Enrichment Program",
    descriptionTranslations: {
      es: "Programa de Enriquecimiento No-Engage 360",
      vi: "Chương trình Làm giàu Non-Engage 360",
      zh_Hans: "Non-Engage 360 enrichment项目",
      pa: "Non-Engage 360 ਸੰਵਰਧਨ ਪ੍ਰੋਗਰਾਮ",
      fa: "برنامه غنی‌سازی Non-Engage 360",
      hmn: "Kev Pab Cuam Kawm Ntxiv Uas Tsis Yog Engage 360",
    },
  },
  {
    code: "ACADEMIC_SUPPORT",
    description: "Academic Support (tutoring, intervention, etc.)",
    descriptionTranslations: {
      es: "Apoyo Académico (tutoría, intervención, etc.)",
      vi: "Hỗ trợ Học tập (gia sư, can thiệp, v.v.)",
      zh_Hans: "学业支持（辅导、干预等）",
      pa: "ਅਕਾਦਮਿਕ ਸਹਾਇਤਾ (ਟਿਊਸ਼ਨ, ਦਖਲ, ਆਦਿ)",
      fa: "حمایت تحصیلی (تدریس خصوصی، مداخله و غیره)",
      hmn: "Kev Pab Kev Kawm (kev qhia ntxiv, kev pab cuam, thiab lwm yam)",
    },
  },
  {
    code: "FAMILY_EMERGENCY",
    description: "Family Emergency",
    descriptionTranslations: {
      es: "Emergencia Familiar",
      vi: "Trường hợp Khẩn cấp Gia đình",
      zh_Hans: "家庭紧急情况",
      pa: "ਪਰਿਵਾਰਕ ਐਮਰਜੈਂਸੀ",
      fa: "فوریت خانوادگی",
      hmn: "Xwm Txheej Ceev Hauv Tsev Neeg",
    },
  },
  {
    code: "MEDICAL_APPOINTMENT",
    description: "Medical Appointment",
    descriptionTranslations: {
      es: "Cita Médica",
      vi: "Cuộc hẹn Y tế",
      zh_Hans: "医疗预约",
      pa: "ਡਾਕਟਰੀ ਮੁਲਾਕਾਤ",
      fa: "قرار ملاقات پزشکی",
      hmn: "Kev Teem Caij Kho Mob",
    },
  },
  {
    code: "WEATHER",
    description: "Weather Condition",
    descriptionTranslations: {
      es: "Condición Climática",
      vi: "Điều kiện Thời tiết",
      zh_Hans: "天气状况",
      pa: "ਮੌਸਮ ਦੀ ਸਥਿਤੀ",
      fa: "شرایط آب و هوایی",
      hmn: "Huab Cua",
    },
  },
  {
    code: "CHILD_HEALTH",
    description: "Child Accident/Illness",
    descriptionTranslations: {
      es: "Accidente/Enfermedad del Niño",
      vi: "Tai nạn/Bệnh tật của Trẻ",
      zh_Hans: "儿童意外/疾病",
      pa: "ਬੱਚੇ ਦਾ ਹਾਦਸਾ/ਬਿਮਾਰੀ",
      fa: "حادثه/بیماری کودک",
      hmn: "Menyuam Raug Xwm Txheej/Muaj Mob",
    },
  },
  {
    code: "WALKER_RELEASE",
    description: "Transportation/Intermediate Walker Release",
    descriptionTranslations: {
      es: "Transporte/Liberación Intermedia de Caminante",
      vi: "Vận chuyển/Thả Đi bộ Trung gian",
      zh_Hans: "交通/中途步行接送",
      pa: "ਆਵਾਜਾਈ/ਇੰਟਰਮੀਡੀਏਟ ਵਾਕਰ ਰਿਲੀਜ਼",
      fa: "حمل‌ونقل/ترخیص پیاده‌روی متوسطه",
      hmn: "Kev Thauj Mus Los/Kev Tso Tub Ntxhais Kawm Theem Nrab Mus Ko Taw",
    },
  },
  {
    code: "HIGHSCHOOL_EXEMPT",
    description: "High School Exempt",
    descriptionTranslations: {
      es: "Exento de Preparatoria",
      vi: "Miễn trừ Trung học Phổ thông",
      zh_Hans: "高中豁免",
      pa: "ਹਾਈ ਸਕੂਲ ਛੋਟ",
      fa: "معافیت دبیرستان",
    },
  },
  {
    code: "HIGH_IMPACT_TUTORING",
    description: "High Impact Tutoring",
    descriptionTranslations: {
      es: "Tutoría de Alto Impacto",
      vi: "Gia sư Có Tác động Cao",
      zh_Hans: "高效辅导",
      pa: "ਉੱਚ ਪ੍ਰਭਾਵ ਟਿਊਸ਼ਨ",
      fa: "تدریس خصوصی با تأثیر بالا",
    },
  },
  {
    code: "OTHER",
    description: "Other Reason",
    descriptionTranslations: {
      es: "Otra Razón",
      vi: "Lý do Khác",
      zh_Hans: "其他原因",
      pa: "ਹੋਰ ਕਾਰਨ",
      fa: "دلیل دیگر",
      hmn: "Lwm Yam Vim",
    },
  },
];

export default EARLY_RELEASE_CODE_DESCRIPTION;

export const LATE_ARRIVAL_CODE_DESCRIPTION = {
  NON_ENGAGE360: "Non-Engage 360 Enrichment Program",
  ACADEMIC_SUPPORT: "Academic Support (tutoring, intervention, etc.)",
  FAMILY_EMERGENCY: "Family Emergency",
  MEDICAL_APPOINTMENT: "Medical Appointment",
  WEATHER: "Weather Condition",
  CHILD_HEALTH: "Child Accident/Illness",
  WALKER_RELEASE: "Transportation",
  HIGHSCHOOL_EXEMPT: "High School Exempt",
  HIGH_IMPACT_TUTORING: "High Impact Tutoring",
  OTHER: "Other Reason",
};

function publicBase(): string {
  return getCurrentTenantConfig().publicBase ?? process.env.NEXT_PUBLIC_BASE ?? "";
}

/**
 * Single source of tenant identity on the client: the tenant slug derived from
 * the host. Replaces the per-tenant `is<Tenant>()` helpers — instead of N
 * booleans each doing `publicBase().includes("expanded-learning-<x>.us")`, every
 * tenant-specific branch reads this one token. Post-cutover every tenant resolves
 * a slug, so this is always populated for a real tenant host.
 *
 * Returns the `<x>` token from `expanded-learning-<x>.us` (the EXACT substring the
 * old `is<Tenant>()` helpers matched on — e.g. `sausd`, `wintersjoint`,
 * `sanjosecharteracademy`, `merced`, `oakland`). Returns `""` for non-tenant hosts
 * (staging / localhost / demo / canonical), which is behaviour-identical to the
 * old helpers returning false there — use the env helpers (isStaging/isLocalhost/
 * isDemo) for those, exactly as before.
 *
 * NOTE: this token is the HOST label, which for three tenants differs from the
 * registry slug (sanjosecharteracademy≠san-jose-charter, sausd≠enterprise-sausd,
 * wintersjoint≠winters-joint). Call sites that previously keyed on the host label
 * keep doing so verbatim — this is a mechanical, zero-regression swap.
 */
export function getTenantSlug(): string {
  const match = publicBase().match(/expanded-learning-([a-z0-9]+)\.us/);
  return match ? match[1]! : "";
}

export function isStaging(): boolean {
  return publicBase().includes("admin.attendlyafterschool.live");
}

export function isLocalhost(): boolean {
  return publicBase().includes("localhost") || (typeof window !== "undefined" && window.location?.hostname === "localhost");
}

/** True on a sandbox tenant host (`admin.sandbox-*.attendly.com`). Real districts stay false. */
export function isSandboxHost(): boolean {
  if (publicBase().includes("sandbox-")) return true;
  return typeof window !== "undefined" && window.location.hostname.includes("sandbox-");
}

export function isSAUSDOrStaging(): boolean {
  return getTenantSlug() === "sausd" || isStaging() || isLocalhost();
}

/**
 * Santana / Enterprise-SAUSD. Covers the legacy expanded-learning host
 * (`getTenantSlug() === "sausd"`) and the consolidated hosts
 * `admin.sausd.attendly.com` / `enterprise-sausd.attendly.com`, which
 * getTenantSlug() does not resolve. Used to scope the CP lottery flag
 * toggle (ATT-4096) the same way Meal Service stays Beaumont-only until
 * its flag is on.
 */
export function isSausd(): boolean {
  return (
    getTenantSlug() === "sausd" ||
    publicBase().includes("sausd.attendly.com")
  );
}

export function isDemo(): boolean {
  return publicBase().includes("admin.meetenrich.com") || isStaging();
}

/**
 * Thermalito (ATT-3359) is a consolidated-native tenant served at the canonical
 * `admin.thermalito.attendly.com` host, which getTenantSlug() does NOT resolve
 * (it only parses legacy `expanded-learning-<x>.us` hosts). This env-style helper
 * lets the canonical host be recognized where needed (contact labeling) without
 * touching the shared getTenantSlug() contract.
 */
export function isThermalito(): boolean {
  return publicBase().includes("thermalito.attendly.com");
}

/**
 * Tracy (ATT-3532) is a consolidated-native tenant served at the canonical
 * `admin.tracy.attendly.com` host, which getTenantSlug() does NOT resolve
 * (it only parses legacy `expanded-learning-<x>.us` hosts). Same pattern as
 * Thermalito.
 */
export function isTracy(): boolean {
  return publicBase().includes("tracy.attendly.com");
}

/**
 * Twin Rivers Charter (ATT-3701) is a consolidated-native tenant served at the
 * canonical `admin.twinrivers.attendly.com` host, which getTenantSlug() does NOT
 * resolve (it only parses legacy `expanded-learning-<x>.us` hosts). Same pattern
 * as Thermalito and Tracy.
 */
export function isTwinRivers(): boolean {
  return publicBase().includes("twinrivers.attendly.com");
}

/**
 * Yucaipa-Calimesa JUSD (ATT-3706) is a consolidated-native tenant served at the
 * canonical `admin.ycjusd.attendly.com` host, which getTenantSlug() does NOT
 * resolve. Same pattern as Thermalito / Tracy / Twin Rivers.
 *
 * NOTE: `ycjusd` (Yucaipa-Calimesa) is a different district from `ycusd`
 * (Yuba City USD) — do not conflate the two slugs.
 */
export function isYucaipa(): boolean {
  return publicBase().includes("ycjusd.attendly.com");
}

/**
 * Esparto USD (ATT-3784) is a consolidated-native tenant served at the canonical
 * `admin.esparto.attendly.com` host, which getTenantSlug() does NOT resolve.
 * Same pattern as Thermalito / Tracy / Twin Rivers / Yucaipa.
 */
export function isEsparto(): boolean {
  return publicBase().includes("esparto.attendly.com");
}

/**
 * Le Grand UESD (ATT-3715) is a consolidated-native tenant served at the
 * canonical `admin.legrand.attendly.com` host, which getTenantSlug() does NOT
 * resolve. Same pattern as Thermalito / Tracy / Twin Rivers / Yucaipa.
 */
export function isLeGrand(): boolean {
  return publicBase().includes("legrand.attendly.com");
}

/**
 * Caliber Public Schools (ATT-3911) — consolidated-native PowerSchool tenant
 * at the canonical `admin.caliber.attendly.com` host. Same pattern as Le Grand.
 */
export function isCaliber(): boolean {
  return publicBase().includes("caliber.attendly.com");
}

/**
 * Romoland is a legacy `expanded-learning-romoland.us` tenant, so its slug
 * resolves via getTenantSlug(). Used to scope Romoland-only surfaces (the
 * Learning Recovery Diagnostics / Interventions cards on the student page).
 * Returns false on staging/localhost/canonical hosts (slug ""), which is the
 * intended behaviour — those are not Romoland.
 */
export function isRomoland(): boolean {
  return getTenantSlug() === "romoland";
}

export const FIVE_MINUTES_MS = 1000 * 60 * 5;
export const TWO_MINUTES_MS = 1000 * 60 * 2;
export const USER_COOKIE_NAME = "__secure.user-session-token";
export const DEFAULT_MAX_AGE = 5 * 60; // 5 minutes in seconds

// Only BSP has a backend late-arrival default (see api Program.lateArrivalTriggerTime,
// where the ASP entry was intentionally removed in #434). Keep the UI consistent.
export const BSP_DEFAULT_PROGRAM_LATE_ARRIVAL_MINUTES_LIMIT = 15; // 15 minutes

export const MAX_SEARCH_RESULTS = 5000; // artifically big page size to avoid pagination
